import React, { useEffect, useMemo, useState } from "react";
import cloneDeep from "lodash/cloneDeep";
import Confetti from "react-dom-confetti";
import { RouteComponentProps } from "react-router-dom";
import useKeypress from "react-use-keypress";
import styled from "styled-components";
import {
  Button,
  ButtonGroup,
  Drawer,
  Position,
  Tab,
  Tabs,
} from "@blueprintjs/core";

import { AppNavbar, AppToaster, CodeEditor, PatternEditor } from "@components";
import { useRunResponse } from "@hooks";
import { CliMatch } from "@types";
import {
  DEFAULT_LANG,
  makeGhIssueUrl,
  SEMGREP_REPO,
  withToaster,
} from "@utils";

import { run } from "@pages/editor/api";
import { EMPTY_RUN_RESPONSE } from "@pages/editor/constants";
import { Highlight, Snippet } from "@pages/editor/types";
import { makeMatchHighlight } from "@pages/editor/utils";
import {
  ADVANCED_LESSONS,
  ADVANCED_REWARDS,
  BASIC_LESSONS,
  BASIC_REWARDS,
  CodePanel,
  COMPOSITION_LESSONS,
  COMPOSITION_REWARDS,
  LearnPanel,
  Results,
  ResultsPanel,
  RulePanel,
} from "@pages/learn/components";

import "./EditorHome.scss";
import "./Learn.scss";

const config = {
  angle: 90,
  spread: 149,
  startVelocity: 44,
  elementCount: 70,
  dragFriction: 0.15,
  duration: 2000,
  stagger: 3,
  width: "6px",
  height: "19px",
  perspective: "946px",
  colors: ["#a864fd", "#29cdff", "#78ff44", "#ff718d", "#fdff6a"],
};

type LearnParams = { tutorial: string; step: string };

const EMPTY_HIGHLIGHTS: Highlight[] = [];

const Container = styled.main`
  display: grid;
  padding: 10px 20px 30px;
  grid-gap: 10px;
  width: 95vw;

  /* We explicitly set min column size to 0, otherwise children's huge min-width would apply */
  grid-template:
    "learn"
    "rule" minmax(160px, max-content)
    "code" minmax(420px, max-content)
    "results"
    / minmax(0, 1fr);

  @media (min-width: 1230px) {
    height: calc(100vh - var(--global-spacing-navbar-height));
    grid-template:
      "learn rule results" minmax(200px, auto)
      "learn code results" minmax(200px, 3fr)
      / 400px minmax(0, 2fr) minmax(420px, 1fr);
  }
`;

const matchForPattern = (pattern: string, answers: string[]) => {
  /* Checks if pattern matches any of the verified answers, disregarding whitespace */
  const patternSan = pattern.replace(/\s/g, "").toLowerCase();
  const answersSan = answers.map((answer) =>
    answer.replace(/\s/g, "").toLowerCase()
  );
  return answersSan.includes(patternSan);
};

const reportBug = () => {
  const ghIssueReport = makeGhIssueUrl(
    SEMGREP_REPO,
    "semgrep.dev bug report",
    window.location.href
  );
  window.open(ghIssueReport, "_blank");
};

const getLessons = (tutorial: string) => {
  switch (tutorial) {
    case "basics":
      return BASIC_LESSONS;
    case "composition":
      return COMPOSITION_LESSONS;
    case "advanced":
      return ADVANCED_LESSONS;
    default:
      return BASIC_LESSONS;
  }
};

const getRewards = (tutorial: string) => {
  switch (tutorial) {
    case "basics":
      return BASIC_REWARDS;
    case "composition":
      return COMPOSITION_REWARDS;
    case "advanced":
      return ADVANCED_REWARDS;
    default:
      return BASIC_REWARDS;
  }
};

export const Learn: React.FC<RouteComponentProps<LearnParams>> = ({
  history,
  match: {
    params: { tutorial, step },
  },
}) => {
  const LESSONS = getLessons(tutorial);
  const REWARDS = getRewards(tutorial);
  const [celebrate, setCelebrate] = useState(false);
  const [centeredLine, setCenteredLine] = useState<number>();
  const [challengesComplete, setChallengesComplete] = useState<number[]>([]);
  const [dirty, setDirty] = useState(false);
  const [hasShownFollowup, setHasShownFollowup] = useState(false);
  const [highlighted, setHighlighted] = useState<Highlight[]>(EMPTY_HIGHLIGHTS);
  const [isRunning, setIsRunning] = useState(false);
  const [language, setLanguage] = useState(LESSONS[0].language || DEFAULT_LANG);
  const [pattern, setPattern] = useState(LESSONS[0].pattern);
  const [runResult, setRunResult] = useState(EMPTY_RUN_RESPONSE);
  const [target, setTarget] = useState(LESSONS[0].target);

  useKeypress("Enter", (event) => {
    if (event.metaKey) {
      runAsync();
    }
  });

  const isSinglePattern = useMemo(() => !pattern.includes("rules:"), [pattern]);

  const { findings, patternErrors, targetErrors } = useRunResponse(runResult);

  const highlightResults = useMemo(
    () => (results: CliMatch[]) => {
      if (results.length > 0) {
        const firstRes = results[0];
        // Make sure line is visible within editor (will scroll to it)
        setCenteredLine(firstRes.start.line);
        const highlighted = results.map((r) => makeMatchHighlight(r));
        setHighlighted(highlighted);
      }
    },
    [setCenteredLine, setHighlighted]
  );

  const learnId = useMemo(() => {
    if (step !== undefined) {
      const stepIx = parseInt(step);
      if (!isNaN(stepIx) && stepIx > 0 && stepIx <= LESSONS.length) {
        return stepIx - 1; // convert from 1 -> 0 indexed
      } else {
        history.replace(`/learn/${tutorial}/1`);
        return 0;
      }
    } else {
      return 0;
    }
  }, [step, history, tutorial, LESSONS.length]);

  const learnRun = useMemo(
    () => (callback?: () => void) => {
      const snippet: Snippet = { pattern, language, target };
      setIsRunning(true);
      setRunResult(EMPTY_RUN_RESPONSE);
      if (pattern.trim().length === 0) {
        AppToaster.show({
          message: "You can't run semgrep with an empty pattern",
          intent: "danger",
        });
        setIsRunning(false);
      } else if (target.trim().length === 0) {
        AppToaster.show({
          message: "You can't run semgrep with an empty code block",
          intent: "danger",
        });
        setIsRunning(false);
      } else {
        withToaster(
          () =>
            run(snippet, !isSinglePattern, {}).then((data) => {
              const correct = matchForPattern(
                pattern,
                LESSONS[learnId].answers
              );
              let nowComplete = cloneDeep(challengesComplete);
              if (correct && !nowComplete.includes(learnId)) {
                nowComplete.push(learnId);
              }
              setRunResult(data);
              setDirty(false);
              setCelebrate(correct);
              setChallengesComplete(nowComplete);
              highlightResults(data.semgrep_result.output.results);
              if (typeof callback === "function") {
                callback();
              }
            }),
          (err) => {
            const msg = `Error running semgrep:\n${err.body?.error}`;
            return <div style={{ whiteSpace: "pre-wrap" }}>{msg}</div>;
          },
          { timeout: 30000 }
        )().finally(() => {
          setIsRunning(false);
        });
      }
    },
    [
      learnId,
      pattern,
      language,
      target,
      isSinglePattern,
      challengesComplete,
      highlightResults,
      setRunResult,
      setDirty,
      setCelebrate,
      setChallengesComplete,
      LESSONS,
    ]
  );

  const runAsync = useMemo(
    () => async (): Promise<void> => {
      return await new Promise((resolve) => {
        learnRun(() => {
          resolve();
        });
      });
    },
    [learnRun]
  );

  const resetOnChange = useMemo(
    () => () => {
      if (findings.length > 0) {
        setHighlighted(EMPTY_HIGHLIGHTS);
        setDirty(true);
      } else {
        setHighlighted(EMPTY_HIGHLIGHTS);
        setDirty(true);
        setRunResult(EMPTY_RUN_RESPONSE);
      }
    },
    [findings, setHighlighted, setDirty]
  );

  const onChangePattern = useMemo(
    () => (pattern: string) => {
      resetOnChange();
      setPattern(pattern);
      LESSONS[learnId].savedwork = pattern;
    },
    [learnId, resetOnChange, setPattern, LESSONS]
  );

  const onChangeTarget = useMemo(
    () => (target: string) => {
      resetOnChange();
      setTarget(target);
    },
    [resetOnChange, setTarget]
  );

  const handleResultClick = useMemo(
    () => (line?: number) => () => {
      setCenteredLine(line);
    },
    [setCenteredLine]
  );

  const advance = useMemo(
    () =>
      (tutorial: string, step: number = 1) =>
      () => {
        setCelebrate(false);
        setHasShownFollowup(false);
        history.push(`/learn/${tutorial}/${learnId + step + 1}`);
      },
    [learnId, history, setCelebrate, setHasShownFollowup]
  );

  useEffect(() => {
    // Clear edit state after changing lesson
    resetOnChange();
  }, [learnId, resetOnChange]);

  useEffect(() => {
    // Want this to only change on learn ID to prevent resets
    setPattern(LESSONS[learnId].pattern);
    setTarget(LESSONS[learnId].target);
    setLanguage(LESSONS[learnId].language || DEFAULT_LANG);
  }, [learnId, LESSONS]);

  return (
    <div>
      <AppNavbar />
      {LESSONS[learnId].followup && !hasShownFollowup && (
        <Drawer
          style={{
            width: "400px",
            alignSelf: "center",
            marginLeft: "5px",
            marginTop: "45px",
            overflow: "auto",
          }}
          isOpen={celebrate}
          onClose={() => {
            setCelebrate(false);
            setHasShownFollowup(true);
          }}
          title={(LESSONS[learnId].followupTitle ?? "Nice Work!") + " 🎉"}
          position={Position.TOP}
          size={"400px"}
          hasBackdrop={false}
          autoFocus={false}
          isCloseButtonShown={true}
        >
          <div style={{ padding: "10px" }}>{LESSONS[learnId].followup}</div>
          {learnId < LESSONS.length - 1 && (
            <Button
              style={{
                margin: "10px",
                width: "50px",
                position: "relative",
                bottom: 0,
                right: 0,
              }}
              minimal
              intent="primary"
              onClick={advance(tutorial, 1)}
            >
              next
            </Button>
          )}
        </Drawer>
      )}
      <Container>
        <LearnPanel className="learn">
          <h1 className="title">
            {learnId + 1}
            {". "}
            {LESSONS[learnId].title}
            {challengesComplete.includes(learnId) && " ✅"}
          </h1>
          <div className="learn-contents">{LESSONS[learnId].content}</div>
          <div className="learn-actions">
            {/* allow user to move off first lesson only after clicking Run */}
            <ButtonGroup>
              <Button
                minimal
                intent="primary"
                disabled={learnId === 0}
                onClick={() => history.push(`/learn/${tutorial}/1`)}
              >
                first
              </Button>
              <Button
                style={{ width: "40px" }}
                intent="primary"
                icon="chevron-left"
                disabled={learnId === 0}
                onClick={advance(tutorial, -1)}
              />
              <Button
                style={{ width: "40px" }}
                intent="primary"
                icon="chevron-right"
                onClick={advance(tutorial, 1)}
                // Force users to press Run at least once
                disabled={learnId >= LESSONS.length - 1}
              />
              <Button
                minimal
                intent="primary"
                onClick={() =>
                  history.push(`/learn/${tutorial}/${LESSONS.length}`)
                }
                disabled={learnId >= LESSONS.length - 1}
              >
                last
              </Button>
            </ButtonGroup>
          </div>
          <Button
            minimal
            style={{ marginTop: "10px" }}
            intent="primary"
            onClick={() => history.push(`/learn`)}
          >
            all tutorials
          </Button>
        </LearnPanel>
        <RulePanel className="rule-editor">
          {isSinglePattern ? (
            <h3> SEMGREP PATTERN </h3>
          ) : (
            <h3> SEMGREP RULE </h3>
          )}
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Tabs
              id="PatternsTab"
              className="tabs"
              onChange={() => {}}
              selectedTabId={isSinglePattern ? "single" : "yaml"}
            >
              <Tab
                id="single"
                title="Simple"
                panel={undefined}
                disabled={!isSinglePattern}
              />
              <Tab
                id="yaml"
                title="Advanced"
                panel={undefined}
                disabled={isSinglePattern}
              />
            </Tabs>
            <Button
              minimal
              intent="danger"
              style={{ marginLeft: "20px", float: "right" }}
              icon="eraser"
              onClick={() => setPattern(LESSONS[learnId].pattern)}
            >
              Clear Work
            </Button>
          </div>
          <PatternEditor
            pattern={pattern}
            onPatternChange={onChangePattern}
            keyComboRunAction={learnRun}
            language={isSinglePattern ? language : "yaml"}
            error={!dirty && patternErrors.length > 0}
          />
        </RulePanel>
        <CodePanel>
          <h3> TEST CODE </h3>
          <Confetti active={celebrate} config={config} />
          <CodeEditor
            target={target}
            error={!dirty ? targetErrors[0]?.message : undefined}
            language={language}
            highlighted={highlighted}
            centeredLine={centeredLine}
            onTargetChange={onChangeTarget}
            onRun={runAsync}
            isRunning={isRunning}
            keyComboRunAction={learnRun}
          />
        </CodePanel>
        <ResultsPanel>
          <div className={"learnresults"}>
            <Results
              hasRun={true} /// TODO: fixme
              pattern={pattern}
              runResponse={runResult}
              dirty={dirty}
              loading={isRunning}
              onRun={learnRun}
              reportBug={reportBug}
              onFindingClick={handleResultClick}
            />
          </div>
          {!isRunning && !dirty && !celebrate && !hasShownFollowup && (
            <Button
              style={{ marginTop: "5px", width: "105px", height: "30px" }}
              small
              minimal
              onClick={() => setPattern(LESSONS[learnId].answers[0])}
              intent="primary"
            >
              <h5>
                <u>Show Answer</u>
              </h5>
            </Button>
          )}
          <div className="rewards">
            <h3>PROGRESS</h3>
          </div>
          <div className="rewards2">{REWARDS[challengesComplete.length]}</div>
        </ResultsPanel>
      </Container>
    </div>
  );
};
